import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI } from '../services/api';

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);

    const fetchUsers = useCallback(() => {
        setLoading(true);
        usersAPI.getAll()
            .then(res => {
                setUsers(res.data.users);
                setError(null);
            })
            .catch(err => setError(err.response?.data?.error || 'Erro ao carregar usuarios'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleAction = async (userId, action) => {
        setActionLoading(`${userId}-${action}`);
        try {
            if (action === 'delete') {
                await usersAPI.delete(userId);
            } else {
                await usersAPI[action](userId);
            }
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao executar acao');
        } finally {
            setActionLoading(null);
        }
    };

    const getStatusBadge = (status) => {
        const map = {
            approved: 'success',
            pending: 'pending',
            blocked: 'error',
        };
        return map[status] || 'pending';
    };

    const getRoleBadge = (role) => {
        return role === 'admin' ? 'warning' : 'pending';
    };

    const pendingCount = users.filter(u => u.status === 'pending').length;

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <span>Carregando usuarios...</span>
            </div>
        );
    }

    return (
        <div>
            <div className="card">
                <div className="card-header">
                    <h2 className="card-title">Gerenciamento de Usuarios</h2>
                    <button className="btn btn-secondary btn-sm" onClick={fetchUsers}>
                        Atualizar
                    </button>
                </div>

                {pendingCount > 0 && (
                    <div className="um-pending-alert">
                        <strong>{pendingCount}</strong> usuario{pendingCount > 1 ? 's' : ''} aguardando aprovacao
                    </div>
                )}

                {error && <div className="error">{error}</div>}

                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Usuario</th>
                                <th>Email</th>
                                <th>Funcao</th>
                                <th>Status</th>
                                <th>Ultimo Login</th>
                                <th>Acoes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id} className={user.status === 'pending' ? 'um-row-pending' : ''}>
                                    <td>
                                        <div className="um-user-cell">
                                            {user.picture && (
                                                <img
                                                    src={user.picture}
                                                    alt={user.name}
                                                    className="um-user-avatar"
                                                    referrerPolicy="no-referrer"
                                                />
                                            )}
                                            <span>{user.name}</span>
                                        </div>
                                    </td>
                                    <td>{user.email}</td>
                                    <td>
                                        <span className={`badge ${getRoleBadge(user.role)}`}>
                                            {user.role === 'admin' ? 'Admin' : 'Usuario'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${getStatusBadge(user.status)}`}>
                                            {user.status === 'approved' ? 'Aprovado' : user.status === 'pending' ? 'Pendente' : 'Bloqueado'}
                                        </span>
                                    </td>
                                    <td className="um-date">
                                        {user.last_login
                                            ? new Date(user.last_login + 'Z').toLocaleString('pt-BR')
                                            : 'Nunca'}
                                    </td>
                                    <td>
                                        <div className="um-actions">
                                            {user.status === 'pending' && (
                                                <button
                                                    className="btn btn-success btn-sm"
                                                    onClick={() => handleAction(user.id, 'approve')}
                                                    disabled={actionLoading === `${user.id}-approve`}
                                                >
                                                    Aprovar
                                                </button>
                                            )}
                                            {user.status === 'approved' && user.role !== 'admin' && (
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => handleAction(user.id, 'block')}
                                                    disabled={actionLoading === `${user.id}-block`}
                                                >
                                                    Bloquear
                                                </button>
                                            )}
                                            {user.status === 'blocked' && (
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => handleAction(user.id, 'approve')}
                                                    disabled={actionLoading === `${user.id}-approve`}
                                                >
                                                    Desbloquear
                                                </button>
                                            )}
                                            {user.role === 'user' && user.status === 'approved' && (
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => handleAction(user.id, 'promote')}
                                                    disabled={actionLoading === `${user.id}-promote`}
                                                >
                                                    Promover Admin
                                                </button>
                                            )}
                                            {user.role === 'admin' && (
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => handleAction(user.id, 'demote')}
                                                    disabled={actionLoading === `${user.id}-demote`}
                                                >
                                                    Remover Admin
                                                </button>
                                            )}
                                            {user.role !== 'admin' && (
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => {
                                                        if (window.confirm(`Tem certeza que deseja excluir ${user.name}?`)) {
                                                            handleAction(user.id, 'delete');
                                                        }
                                                    }}
                                                    disabled={actionLoading === `${user.id}-delete`}
                                                >
                                                    Excluir
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="empty-state">
                                        Nenhum usuario cadastrado
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default UserManagement;
